/* eslint-disable @typescript-eslint/no-non-null-assertion */
import toast from 'react-hot-toast';
import { useId, Fragment } from 'react';

import {
	Button,
	useAxios,
	ContentSection,
	DescriptionList,
	DescriptionItem,
	ContentSectionDivider,
} from '@pangea-lis-apps/ui';
import {
	FORMS,
	ASSAYS,
	FieldProps,
	capitalize,
	formatDate,
	ICD_10_CODES,
	requiredStyles,
	getLabelFromValue,
	MiscAndAttachmentsFieldProps,
} from '@pangea-lis-apps/utils';

import { Attachments } from '../../shared/attachments';

const ProviderInformation = ({ data, headingAction }: FieldProps) => {
	if (data.type === 'clinic')
		return (
			<ContentSection
				heading="Provider Info"
				headingAction={headingAction}
			>
				<DescriptionList>
					<DescriptionItem
						term="First Name"
						termStyles={requiredStyles(
							data.requisition_form.provider_first_name
						)}
						details={data.requisition_form.provider_first_name}
					/>
					<DescriptionItem
						term="Last Name"
						termStyles={requiredStyles(
							data.requisition_form.provider_last_name
						)}
						details={data.requisition_form.provider_last_name}
					/>
					<DescriptionItem
						term="National Provider Identifier (NPI)"
						termStyles={requiredStyles(
							data.requisition_form
								.provider_national_provider_identifier
						)}
						details={
							data.requisition_form
								.provider_national_provider_identifier
						}
					/>
				</DescriptionList>
			</ContentSection>
		);

	return (
		<ContentSection heading="Provider Info" headingAction={headingAction}>
			<DescriptionList>
				<DescriptionItem
					term="Name"
					details={data.customer.first_name.concat(
						' ',
						data.customer.last_name
					)}
				/>
			</DescriptionList>
		</ContentSection>
	);
};

const OrganizationInformation = ({ data, headingAction }: FieldProps) => {
	if (data.type === 'clinic')
		return (
			<ContentSection
				heading="Organization Info"
				headingAction={headingAction}
			>
				<DescriptionList>
					<DescriptionItem
						term="Name"
						details={data.requisition_form.organization_name}
						termStyles={requiredStyles(
							data.requisition_form.organization_name
						)}
					/>
					<DescriptionItem
						term="National Provider Identifier (NPI)"
						termStyles={requiredStyles(
							data.requisition_form
								.organization_national_provider_identifier!
						)}
						details={
							data.requisition_form
								.organization_national_provider_identifier
						}
					/>
					<DescriptionItem
						term="Phone Number"
						termStyles={requiredStyles(
							data.requisition_form.organization_phone_number
						)}
						details={
							data.requisition_form.organization_phone_number
						}
					/>
					<DescriptionItem
						termStyles={requiredStyles(
							data.requisition_form.organization_address_address
						)}
						term="Address"
						details={
							data.requisition_form.organization_address_address
						}
					/>
					<DescriptionItem
						term="Address Line 2"
						details={
							data.requisition_form.organization_address_address2
						}
					/>
					<div className="sm:col-span-1"></div>
					<DescriptionItem
						term="City"
						termStyles={requiredStyles(
							data.requisition_form.organization_address_city
						)}
						details={
							data.requisition_form.organization_address_city
						}
					/>
					<DescriptionItem
						term="State"
						termStyles={requiredStyles(
							data.requisition_form.organization_address_state
						)}
						details={
							data.requisition_form.organization_address_state
						}
					/>
					<DescriptionItem
						term="ZIP Code"
						termStyles={requiredStyles(
							data.requisition_form.organization_address_zip_code
						)}
						details={
							data.requisition_form.organization_address_zip_code
						}
					/>
				</DescriptionList>
			</ContentSection>
		);

	return (
		<ContentSection
			heading="Organization Info"
			headingAction={headingAction}
		>
			<DescriptionList>
				<DescriptionItem term="Name" details={data.organization.name} />
				<DescriptionItem
					term="Phone Number"
					details={data.organization.phone_number}
				/>
				<DescriptionItem term="Address">
					<p>
						{data.organization.address}
						<br />
						{data.organization.city}, {data.organization.state}{' '}
						{data.organization.zip_code}
					</p>
				</DescriptionItem>
			</DescriptionList>
		</ContentSection>
	);
};

const OrderAndSpecimenInformation = ({
	data,
	hide,
	headingAction,
}: FieldProps) => {
	if (data.type === 'clinic')
		return (
			<ContentSection
				heading="Order and specimen information"
				headingAction={headingAction}
			>
				<DescriptionList>
					<DescriptionItem
						term="Assay"
						details={getLabelFromValue(
							data.requisition_form.sample_assay,
							ASSAYS['assays']
						)}
					/>
					{!(hide && hide.includes('ICD-10 Code(s)')) && (
						<Fragment>
							<div className="sm:col-span-2"></div>
							<DescriptionItem
								term="ICD-10 Code(s)"
								termStyles={requiredStyles(
									data.requisition_form.order_icd_10_codes
								)}
							>
								{data.requisition_form.order_icd_10_codes
									.length ? (
									<ul className="list-disc list-inside">
										{data.requisition_form.order_icd_10_codes.map(
											(code: string, index: number) => (
												<li key={index}>
													{getLabelFromValue(
														code,
														ICD_10_CODES.codes
															.precision_biome_wound
													)}
												</li>
											)
										)}
									</ul>
								) : null}
							</DescriptionItem>
						</Fragment>
					)}
					{!(
						hide && hide.includes('Provider Signature Provided')
					) && (
						<Fragment>
							<div className="sm:col-span-2"></div>
							<DescriptionItem
								term="Provider Signature Provided"
								termStyles={requiredStyles(
									data.requisition_form
										.order_ordering_provider_signature_provided
								)}
								details={
									data.requisition_form
										.order_ordering_provider_signature_provided
								}
							/>
						</Fragment>
					)}
					{!(hide && hide.includes('Date Provided')) && (
						<DescriptionItem
							term="Date Provided"
							termStyles={requiredStyles(
								data.requisition_form
									.order_ordering_provider_date
							)}
							details={formatDate(
								data.requisition_form
									.order_ordering_provider_date
							)}
						/>
					)}
					{!(hide && hide.includes('Collection Date')) && (
						<DescriptionItem
							term="Collection Date"
							termStyles={requiredStyles(
								data.requisition_form.sample_collection_date
							)}
							details={formatDate(
								data.requisition_form.sample_collection_date
							)}
						/>
					)}
				</DescriptionList>
			</ContentSection>
		);

	return (
		<ContentSection
			heading="Order information"
			headingAction={headingAction}
		>
			<DescriptionList>
				<DescriptionItem
					term="Assay"
					details={getLabelFromValue(
						data.sample.assay,
						ASSAYS['assays']
					)}
				/>
				<DescriptionItem
					term="Collection Date"
					details={formatDate(data.sample.sample_collection_date)}
				/>
			</DescriptionList>
		</ContentSection>
	);
};

const PatientInformation = ({ data, headingAction }: FieldProps) => {
	if (data.type === 'clinic')
		return (
			<ContentSection
				headingAction={headingAction}
				heading="Provided patient information"
			>
				<DescriptionList>
					<DescriptionItem
						term="First Name"
						termStyles={requiredStyles(
							data.requisition_form.patient_first_name
						)}
						details={data.requisition_form.patient_first_name}
					/>
					<DescriptionItem
						term="Middle Name"
						details={data.requisition_form.patient_middle_name}
					/>
					<DescriptionItem
						term="Last Name"
						termStyles={requiredStyles(
							data.requisition_form.patient_last_name
						)}
						details={data.requisition_form.patient_last_name}
					/>
					<DescriptionItem
						term="Medical Record Number (MRN)"
						termStyles={requiredStyles(
							data.requisition_form.patient_medical_record_number
						)}
						details={
							data.requisition_form.patient_medical_record_number
						}
					/>
					<DescriptionItem
						term="Date of Birth"
						termStyles={requiredStyles(
							data.requisition_form.patient_date_of_birth
						)}
						details={formatDate(
							data.requisition_form.patient_date_of_birth
						)}
					/>
					<div className="sm:col-span-1"></div>
					<DescriptionItem
						term="Sex"
						details={
							data.requisition_form.patient_sex &&
							capitalize(data.requisition_form.patient_sex)
						}
					/>
					{/* <DescriptionItem
						term="Ethnicity"
						details={getLabelFromValue(
							data.requisition_form.patient_ethnicity,
							REQUISITION_FORM.ethnicity_options
						)}
					/>
					<DescriptionItem
						term="Race"
						details={data.requisition_form.patient_race
							.map((race: string) =>
								getLabelFromValue(
									race,
									REQUISITION_FORM.race_options
								)
							)
							.join(', ')}
					/> */}
					<DescriptionItem
						termStyles={requiredStyles(
							data.requisition_form.patient_address
						)}
						term="Address"
						details={data.requisition_form.patient_address}
					/>
					<DescriptionItem
						term="Address Line 2"
						details={data.requisition_form.patient_address2}
					/>
					<div className="sm:col-span-1"></div>
					<DescriptionItem
						term="City"
						termStyles={requiredStyles(
							data.requisition_form.patient_city
						)}
						details={data.requisition_form.patient_city}
					/>
					<DescriptionItem
						term="State"
						termStyles={requiredStyles(
							data.requisition_form.patient_state
						)}
						details={data.requisition_form.patient_state}
					/>
					<DescriptionItem
						term="ZIP Code"
						termStyles={requiredStyles(
							data.requisition_form.patient_zip_code
						)}
						details={data.requisition_form.patient_zip_code}
					/>
					<DescriptionItem
						term="Country"
						termStyles={requiredStyles(
							data.requisition_form.patient_country
						)}
						details={getLabelFromValue(
							data.requisition_form.patient_country,
							FORMS.countries
						)}
					/>
					<div className="sm:col-span-2"></div>
					<DescriptionItem
						term="Phone Number"
						details={data.requisition_form.patient_phone_number}
					/>
					<DescriptionItem
						term="Email Address"
						details={data.requisition_form.patient_email_address}
					/>
				</DescriptionList>
			</ContentSection>
		);

	return null;
};

const BillingInformation = ({ data, headingAction }: FieldProps) => {
	if (data.type === 'clinic')
		return (
			<Fragment>
				<ContentSection heading="Billing status">
					<DescriptionList>
						<DescriptionItem
							term="Verified?"
							termStyles={requiredStyles(
								data.requisition_form.billing_verified
							)}
							details={data.requisition_form.billing_verified}
						/>
						<div className="sm:col-span-2"></div>
						<DescriptionItem
							term="Follow Up Required?"
							details={
								data.requisition_form.metadata
									.billing_verification.follow_up.required
							}
						/>
						{data.requisition_form.metadata.billing_verification
							.follow_up.required && (
							<Fragment>
								<DescriptionItem
									term="Follow up with?"
									details={capitalize(
										data.requisition_form.metadata
											.billing_verification.follow_up.with
									)}
								/>
								<div className="sm:col-span-1"></div>
								<DescriptionItem term="Follow up again in?">
									{'Within '}
									{
										data.requisition_form.metadata
											.billing_verification.follow_up
											.in_days
									}
									{' Days'}
								</DescriptionItem>
								<DescriptionItem term="Days since last follow up?">
									{Math.round(
										(new Date().getTime() -
											new Date(
												data.requisition_form.metadata.billing_verification.follow_up.metadata.date?.$date
											).getTime()) /
											(1000 * 3600 * 24)
									)}
									{' Days'}
								</DescriptionItem>
							</Fragment>
						)}
					</DescriptionList>
				</ContentSection>

				<ContentSectionDivider />

				<ContentSection
					heading="Billing information - Primary payer"
					headingAction={headingAction}
				>
					<DescriptionList>
						{/* <DescriptionItem
							term="Primary payer"
							termStyles={requiredStyles(
								data.requisition_form.billing_primary_payer
							)}
							details={getLabelFromValue(
								data.requisition_form.billing_primary_payer,
								REQUISITION_FORM.billing_information_payer_options
							)}
						/> */}
						{data.requisition_form.billing_primary_payer ===
							'other' && (
							<DescriptionItem
								term="Primary payer (if other)"
								termStyles={
									data.requisition_form
										.billing_primary_payer === 'other'
										? requiredStyles(
												data.requisition_form
													.billing_primary_payer_other
										  )
										: ''
								}
								details={
									data.requisition_form
										.billing_primary_payer_other
								}
							/>
						)}
						<DescriptionItem
							term="Primary payer verified?"
							termStyles={requiredStyles(
								data.requisition_form
									.billing_primary_payer_verified
							)}
							details={
								data.requisition_form
									.billing_primary_payer_verified
							}
						/>
					</DescriptionList>
				</ContentSection>

				{data.requisition_form.billing_primary_payer === 'patient' && (
					<ContentSection heading="Payment verification">
						<DescriptionList>
							<DescriptionItem
								term="Patient Payment Received?"
								termStyles={requiredStyles(
									data.requisition_form
										.billing_primary_payer_patient_payment_received
								)}
								details={
									data.requisition_form
										.billing_primary_payer_patient_payment_received
								}
							/>
							{/* <DescriptionItem
								term="Patient Payment Payee"
								termStyles={requiredStyles(
									data.requisition_form
										.billing_primary_payer_patient_payment_payee
								)}
								details={getLabelFromValue(
									data.requisition_form
										.billing_primary_payer_patient_payment_payee,
									REQUISITION_FORM.patient_payment_payee_options
								)}
							/> */}
						</DescriptionList>
					</ContentSection>
				)}

				{['insurance', 'medicare', 'medi-cal'].includes(
					data.requisition_form.billing_primary_payer
				) && (
					<Fragment>
						{data.requisition_form.billing_primary_payer ===
							'insurance' && (
							<ContentSection heading="Primary insurance company information">
								<DescriptionList>
									<DescriptionItem
										term="Name"
										termStyles={requiredStyles(
											data.requisition_form
												.billing_primary_insurance_company_name
										)}
										details={
											data.requisition_form
												.billing_primary_insurance_company_name
										}
									/>
									<div className="sm:col-span-2"></div>
									<DescriptionItem
										term="Address"
										termStyles={requiredStyles(
											data.requisition_form
												.billing_primary_insurance_company_address
										)}
										details={
											data.requisition_form
												.billing_primary_insurance_company_address
										}
									/>
									<div className="sm:col-span-2"></div>
									<DescriptionItem
										term="City"
										termStyles={requiredStyles(
											data.requisition_form
												.billing_primary_insurance_company_city
										)}
										details={
											data.requisition_form
												.billing_primary_insurance_company_city
										}
									/>
									<DescriptionItem
										term="State"
										termStyles={requiredStyles(
											data.requisition_form
												.billing_primary_insurance_company_state
										)}
										details={
											data.requisition_form
												.billing_primary_insurance_company_state
										}
									/>
									<DescriptionItem
										term="ZIP Code"
										termStyles={requiredStyles(
											data.requisition_form
												.billing_primary_insurance_company_zip_code
										)}
										details={
											data.requisition_form
												.billing_primary_insurance_company_zip_code
										}
									/>
								</DescriptionList>
							</ContentSection>
						)}

						<ContentSection heading="Primary insurance subscriber Information">
							<DescriptionList>
								<DescriptionItem
									term="First Name"
									termStyles={requiredStyles(
										data.requisition_form
											.billing_primary_insurance_subscriber_first_name
									)}
									details={
										data.requisition_form
											.billing_primary_insurance_subscriber_first_name
									}
								/>
								<DescriptionItem
									term="Last Name"
									termStyles={requiredStyles(
										data.requisition_form
											.billing_primary_insurance_subscriber_last_name
									)}
									details={
										data.requisition_form
											.billing_primary_insurance_subscriber_last_name
									}
								/>
								<DescriptionItem
									term="Date of Birth"
									termStyles={requiredStyles(
										data.requisition_form
											.billing_primary_insurance_subscriber_date_of_birth
									)}
									details={formatDate(
										data.requisition_form
											.billing_primary_insurance_subscriber_date_of_birth
									)}
								/>
								<DescriptionItem
									term="Phone Number"
									termStyles={requiredStyles(
										data.requisition_form
											.billing_primary_insurance_subscriber_phone_number
									)}
									details={
										data.requisition_form
											.billing_primary_insurance_subscriber_phone_number
									}
								/>
								<DescriptionItem
									term="Patient Relationship to Subscriber"
									termStyles={requiredStyles(
										data.requisition_form
											.billing_primary_insurance_patient_relationship_to_subscriber
									)}
									details={
										data.requisition_form
											.billing_primary_insurance_patient_relationship_to_subscriber &&
										capitalize(
											data.requisition_form
												.billing_primary_insurance_patient_relationship_to_subscriber
										)
									}
								/>
								{data.requisition_form
									.billing_primary_insurance_patient_relationship_to_subscriber ===
								'other' ? (
									<DescriptionItem
										term="Patient Relationship to Subscriber (if other)"
										termStyles={requiredStyles(
											data.requisition_form
												.billing_primary_insurance_patient_relationship_to_subscriber_other
										)}
										details={
											data.requisition_form
												.billing_primary_insurance_patient_relationship_to_subscriber_other
										}
									/>
								) : (
									<div className="sm:col-span-1"></div>
								)}
								<DescriptionItem
									term="Policy ID #"
									termStyles={requiredStyles(
										data.requisition_form
											.billing_primary_insurance_subscriber_policy_id_number
									)}
									details={
										data.requisition_form
											.billing_primary_insurance_subscriber_policy_id_number
									}
								/>
								{data.requisition_form.billing_primary_payer ===
									'insurance' && (
									<Fragment>
										<DescriptionItem
											term="Group #"
											details={
												data.requisition_form
													.billing_primary_insurance_subscriber_group_number
											}
										/>
										<div className="sm:col-span-1"></div>
									</Fragment>
								)}
								<DescriptionItem
									term="Email Address"
									details={
										data.requisition_form
											.billing_primary_insurance_subscriber_email_address
									}
								/>
								<DescriptionItem
									term="Fax Number"
									details={
										data.requisition_form
											.billing_primary_insurance_subscriber_fax_number
									}
								/>
							</DescriptionList>
						</ContentSection>

						<ContentSection heading="Primary insurance verification">
							<DescriptionList>
								<DescriptionItem
									term="Verified as payable?"
									details={
										data.requisition_form
											.billing_primary_insurance_verified
									}
									termStyles={requiredStyles(
										data.requisition_form
											.billing_primary_insurance_verified
									)}
								/>
							</DescriptionList>
						</ContentSection>
					</Fragment>
				)}

				{data.requisition_form.billing_has_second_payer && (
					<Fragment>
						<ContentSectionDivider />

						<ContentSection heading="Billing information - Secondary payer">
							<DescriptionList>
								{/* <DescriptionItem
									term="Secondary payer"
									termStyles={requiredStyles(
										data.requisition_form
											.billing_secondary_payer
									)}
									details={getLabelFromValue(
										data.requisition_form
											.billing_secondary_payer,
										REQUISITION_FORM.billing_information_payer_options
									)}
								/> */}
								{data.requisition_form
									.billing_secondary_payer === 'other' && (
									<DescriptionItem
										term="Secondary payer (if other)"
										termStyles={
											data.requisition_form
												.billing_secondary_payer ===
											'other'
												? requiredStyles(
														data.requisition_form
															.billing_secondary_payer_other
												  )
												: ''
										}
										details={
											data.requisition_form
												.billing_secondary_payer_other
										}
									/>
								)}
								<DescriptionItem
									term="Secondary payer verified?"
									details={
										data.requisition_form
											.billing_secondary_payer_verified
									}
								/>
							</DescriptionList>
						</ContentSection>

						{data.requisition_form.billing_secondary_payer ===
							'patient' && (
							<ContentSection heading="Payment verification">
								<DescriptionList>
									<DescriptionItem
										term="Patient Payment Received?"
										termStyles={requiredStyles(
											data.requisition_form
												.billing_secondary_payer_patient_payment_received
										)}
										details={
											data.requisition_form
												.billing_secondary_payer_patient_payment_received
										}
									/>
									{/* <DescriptionItem
										term="Patient Payment Payee"
										termStyles={requiredStyles(
											data.requisition_form
												.billing_secondary_payer_patient_payment_payee
										)}
										details={getLabelFromValue(
											data.requisition_form
												.billing_secondary_payer_patient_payment_payee,
											REQUISITION_FORM.patient_payment_payee_options
										)}
									/> */}
								</DescriptionList>
							</ContentSection>
						)}

						{['insurance', 'medicare', 'medi-cal'].includes(
							data.requisition_form.billing_secondary_payer
						) && (
							<Fragment>
								{data.requisition_form
									.billing_secondary_payer ===
									'insurance' && (
									<ContentSection heading="Secondary insurance company information">
										<DescriptionList>
											<DescriptionItem
												term="Name"
												termStyles={requiredStyles(
													data.requisition_form
														.billing_secondary_insurance_company_name
												)}
												details={
													data.requisition_form
														.billing_secondary_insurance_company_name
												}
											/>
											<div className="sm:col-span-2"></div>
											<DescriptionItem
												term="Address"
												termStyles={requiredStyles(
													data.requisition_form
														.billing_secondary_insurance_company_address
												)}
												details={
													data.requisition_form
														.billing_secondary_insurance_company_address
												}
											/>
											<div className="sm:col-span-2"></div>
											<DescriptionItem
												term="City"
												termStyles={requiredStyles(
													data.requisition_form
														.billing_secondary_insurance_company_city
												)}
												details={
													data.requisition_form
														.billing_secondary_insurance_company_city
												}
											/>
											<DescriptionItem
												term="State"
												termStyles={requiredStyles(
													data.requisition_form
														.billing_secondary_insurance_company_state
												)}
												details={
													data.requisition_form
														.billing_secondary_insurance_company_state
												}
											/>
											<DescriptionItem
												term="ZIP Code"
												termStyles={requiredStyles(
													data.requisition_form
														.billing_secondary_insurance_company_zip_code
												)}
												details={
													data.requisition_form
														.billing_secondary_insurance_company_zip_code
												}
											/>
										</DescriptionList>
									</ContentSection>
								)}

								<ContentSection heading="Secondary insurance subscriber information">
									<DescriptionList>
										<DescriptionItem
											term="First Name"
											termStyles={requiredStyles(
												data.requisition_form
													.billing_secondary_insurance_subscriber_first_name
											)}
											details={
												data.requisition_form
													.billing_secondary_insurance_subscriber_first_name
											}
										/>
										<DescriptionItem
											term="Last Name"
											termStyles={requiredStyles(
												data.requisition_form
													.billing_secondary_insurance_subscriber_last_name
											)}
											details={
												data.requisition_form
													.billing_secondary_insurance_subscriber_last_name
											}
										/>
										<DescriptionItem
											term="Date of Birth"
											termStyles={requiredStyles(
												data.requisition_form
													.billing_secondary_insurance_subscriber_date_of_birth
											)}
											details={formatDate(
												data.requisition_form
													.billing_secondary_insurance_subscriber_date_of_birth
											)}
										/>
										<DescriptionItem
											term="Phone Number"
											termStyles={requiredStyles(
												data.requisition_form
													.billing_secondary_insurance_subscriber_phone_number
											)}
											details={
												data.requisition_form
													.billing_secondary_insurance_subscriber_phone_number
											}
										/>
										<DescriptionItem
											term="Patient Relationship to Subscriber"
											termStyles={requiredStyles(
												data.requisition_form
													.billing_secondary_insurance_patient_relationship_to_subscriber
											)}
											details={
												data.requisition_form
													.billing_secondary_insurance_patient_relationship_to_subscriber &&
												capitalize(
													data.requisition_form
														.billing_secondary_insurance_patient_relationship_to_subscriber
												)
											}
										/>
										{data.requisition_form
											.billing_secondary_insurance_patient_relationship_to_subscriber ===
										'other' ? (
											<DescriptionItem
												term="Patient Relationship to Subscriber (if other)"
												termStyles={requiredStyles(
													data.requisition_form
														.billing_secondary_insurance_patient_relationship_to_subscriber_other
												)}
												details={
													data.requisition_form
														.billing_secondary_insurance_patient_relationship_to_subscriber_other
												}
											/>
										) : (
											<div className="sm:col-span-1"></div>
										)}
										<DescriptionItem
											term="Policy ID #"
											termStyles={requiredStyles(
												data.requisition_form
													.billing_secondary_insurance_subscriber_policy_id_number
											)}
											details={
												data.requisition_form
													.billing_secondary_insurance_subscriber_policy_id_number
											}
										/>
										{data.requisition_form
											.billing_secondary_payer ===
											'insurance' && (
											<Fragment>
												<DescriptionItem
													term="Group #"
													details={
														data.requisition_form
															.billing_secondary_insurance_subscriber_group_number
													}
												/>
												<div className="sm:col-span-1"></div>
											</Fragment>
										)}
										<DescriptionItem
											term="Email Address"
											details={
												data.requisition_form
													.billing_primary_insurance_subscriber_email_address
											}
										/>
										<DescriptionItem
											term="Fax Number"
											details={
												data.requisition_form
													.billing_primary_insurance_subscriber_fax_number
											}
										/>
									</DescriptionList>
								</ContentSection>

								<ContentSection heading="Secondary insurance verification">
									<DescriptionList>
										<DescriptionItem
											term="Verified as payable?"
											details={
												data.requisition_form
													.billing_secondary_insurance_verified
											}
											termStyles={requiredStyles(
												data.requisition_form
													.billing_secondary_insurance_verified
											)}
										/>
									</DescriptionList>
								</ContentSection>
							</Fragment>
						)}
					</Fragment>
				)}
			</Fragment>
		);

	return null;
};

const MiscAndAttachments = ({
	data,
	entity,
	headingAction,
}: MiscAndAttachmentsFieldProps) => {
	if (data.type === 'clinic')
		return (
			<ContentSection
				heading="Authorization and Attachments"
				description="Patient signature and date must be provided for the requisition form to be considered complete."
				headingAction={headingAction}
			>
				<DescriptionList>
					<Attachments
						data={data}
						entity={entity}
						showRequiredStyles={true}
					/>
				</DescriptionList>
			</ContentSection>
		);

	return null;
};

export const PrecisionBIOMEWoundComponents = {
	BillingInformation,
	PatientInformation,
	ProviderInformation,
	OrganizationInformation,
	OrderAndSpecimenInformation,
	MiscAndAttachments,
};

export default PrecisionBIOMEWoundComponents;
